// src/modules/ai-agent/AIEstimatePrompts.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../layouts/DashboardLayout';
import { getEstimatePrompts, updateEstimatePrompts } from '../estimates/aiEstimateService';
import { FaMagic, FaSave } from 'react-icons/fa';

const Container = styled.div`
  padding: 20px;
  max-width: 900px;
`;

const PageTitle = styled.h1`
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Section = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 6px;
  font-size: 0.9rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface};
  border-radius: 6px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.95rem;
  min-height: 200px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SaveButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  padding: 10px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  background-color: ${props => props.error ? '#fa7f7f22' : '#4caf5022'};
  color: ${props => props.error ? '#fa7f7f' : '#4caf50'};
  font-size: 0.9rem;
`;

const HelpText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.85rem;
  margin-top: 6px;
  line-height: 1.4;
`;

const AIEstimatePrompts = () => {
  const [prompts, setPrompts] = useState({
    estimate_intro_prompt: '',
    estimate_footnote_prompt: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const data = await getEstimatePrompts();
      setPrompts(data);
    } catch (err) {
      console.error('Error loading estimate prompts:', err);
      setMessage({ text: 'Error loading prompts', error: true });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      await updateEstimatePrompts(prompts);
      setMessage({ text: 'Prompts saved successfully!', error: false });
    } catch (err) {
      console.error('Error saving prompts:', err);
      setMessage({ text: 'Error saving prompts', error: true });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Container>
          <PageTitle><FaMagic /> Estimate AI Prompts</PageTitle>
          <p>Loading...</p>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container>
        <PageTitle><FaMagic /> Estimate AI Prompts</PageTitle>

        {message && <Message error={message.error}>{message.text}</Message>}

        <Section>
          <SectionTitle><FaMagic /> Introduction / Description Prompt</SectionTitle>
          <FormGroup>
            <Label>System prompt for generating estimate introductions</Label>
            <TextArea
              value={prompts.estimate_intro_prompt || ''}
              onChange={(e) => setPrompts({ ...prompts, estimate_intro_prompt: e.target.value })}
              placeholder="Enter the prompt template for generating estimate introductions..."
            />
            <HelpText>
              Use <strong>{'{{ITEMS_CONTEXT}}'}</strong> as a placeholder where the quote items will be inserted.
              This prompt instructs the AI on how to write the estimate description/introduction.
            </HelpText>
          </FormGroup>
        </Section>

        <Section>
          <SectionTitle><FaMagic /> Footnotes / Notes Prompt</SectionTitle>
          <FormGroup>
            <Label>System prompt for generating estimate footnotes and disclaimers</Label>
            <TextArea
              value={prompts.estimate_footnote_prompt || ''}
              onChange={(e) => setPrompts({ ...prompts, estimate_footnote_prompt: e.target.value })}
              placeholder="Enter the prompt template for generating estimate footnotes..."
            />
            <HelpText>
              Use <strong>{'{{ITEMS_CONTEXT}}'}</strong> as a placeholder where the quote items will be inserted.
              This prompt instructs the AI on how to write the footnotes, disclaimers, and terms.
            </HelpText>
          </FormGroup>
        </Section>

        <SaveButton onClick={handleSave} disabled={saving}>
          <FaSave /> {saving ? 'Saving...' : 'Save Prompts'}
        </SaveButton>
      </Container>
    </DashboardLayout>
  );
};

export default AIEstimatePrompts;
